import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../Components/PageHeader';
import { eleveService, classeService } from '../services/eleveService';

/**
 * Page de sélection d'élève pour voir sa progression
 */
const StudentProgressionSelector = () => {
  const navigate = useNavigate();
  const [eleves, setEleves] = useState([]);
  const [filteredEleves, setFilteredEleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClasse, setSelectedClasse] = useState('');
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    const loadEleves = async () => {
      setLoading(true);
      try {
        // Appel API réel pour récupérer les élèves
        const data = await eleveService.getAll();
        console.log('Élèves récupérés:', data);
        setEleves(data.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des élèves:', error);
        toast.error('Erreur lors du chargement des élèves');
        // En cas d'erreur, utiliser des données d'exemple
        const exempleEleves = [
          {
            id: 1,
            nom: "Dupont",
            prenom: "Marie",
            classe: { id: 1, nom: "6ème A" },
            photo: null
          },
          {
            id: 2,
            nom: "Martin",
            prenom: "Pierre", 
            classe: { id: 1, nom: "6ème A" },
            photo: null
          },
          {
            id: 3,
            nom: "Bernard",
            prenom: "Sophie",
            classe: { id: 2, nom: "5ème B" },
            photo: null
          },
          {
            id: 4,
            nom: "Leroy",
            prenom: "Thomas",
            classe: { id: 2, nom: "5ème B" },
            photo: null
          },
          {
            id: 5,
            nom: "Moreau",
            prenom: "Julie",
            classe: { id: 3, nom: "4ème C" },
            photo: null
          }
        ];
        setEleves(exempleEleves);
      } finally {
        setLoading(false);
      }
    };

    const loadClasses = async () => {
      try {
        // Appel API réel pour récupérer les classes
        const data = await classeService.getAll();
        console.log('Classes récupérées:', data);
        setClasses(data.data.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des classes:', error);
        // En cas d'erreur, utiliser des données d'exemple
        const exempleClasses = [
          { id: 1, nom: "6ème A" },
          { id: 2, nom: "5ème B" },
          { id: 3, nom: "4ème C" }
        ];
        setClasses(exempleClasses);
      }
    };

    loadEleves();
    loadClasses();
  }, []);

  useEffect(() => {
    const filterEleves = () => {
      let filtered = eleves;

      // Filtrer par terme de recherche
      if (searchTerm) {
        filtered = filtered.filter(eleve => {
          const nom = eleve.nom || eleve.nom_utilisateur || '';
          const prenom = eleve.prenom || eleve.prenom_utilisateur || '';
          return nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 prenom.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }

      // Filtrer par classe
      if (selectedClasse) {
        filtered = filtered.filter(eleve => {
          // Gérer différentes structures possibles pour la classe
          const classeId = eleve.classe?.id || eleve.id_classe || eleve.classe_id;
          return classeId && classeId.toString() === selectedClasse;
        });
      }

      setFilteredEleves(filtered);
    };

    filterEleves();
  }, [searchTerm, selectedClasse, eleves]);

  const handleEleveSelect = (eleveId) => {
    navigate(`/dashboard/progression-eleve/${eleveId}`);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <PageHeader title="Progression des Élèves" subTitle="Chargement..." />
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <PageHeader 
        title="Progression des Élèves" 
        subTitle="Sélectionnez un élève pour voir sa progression détaillée"
      />

      {/* Filtres de recherche */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Rechercher un élève</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nom ou prénom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Filtrer par classe</label>
              <select
                className="form-select"
                value={selectedClasse}
                onChange={(e) => setSelectedClasse(e.target.value)}
              >
                <option value="">Toutes les classes</option>
                {classes.map(classe => {
                  const classeId = classe.id || classe.id_classe;
                  const classeNom = classe.nom || classe.nom_classe || classe.libelle || `Classe ${classeId}`;
                  return (
                    <option key={classeId} value={classeId}>
                      {classeNom}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedClasse('');
                }}
              >
                <i className="bi bi-x-lg me-1"></i>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-primary mb-2">
                <i className="bi bi-people-fill fs-1"></i>
              </div>
              <h3 className="card-title text-primary">{eleves.length}</h3>
              <p className="card-text text-muted mb-0">Total élèves</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-info mb-2">
                <i className="bi bi-building fs-1"></i>
              </div>
              <h3 className="card-title text-info">{classes.length}</h3>
              <p className="card-text text-muted mb-0">Classes</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-success mb-2">
                <i className="bi bi-funnel-fill fs-1"></i>
              </div>
              <h3 className="card-title text-success">{filteredEleves.length}</h3>
              <p className="card-text text-muted mb-0">Résultats filtrés</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-warning mb-2">
                <i className="bi bi-graph-up fs-1"></i>
              </div>
              <h3 className="card-title text-warning">
                <i className="bi bi-cursor-fill"></i>
              </h3>
              <p className="card-text text-muted mb-0">Cliquez pour voir</p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des élèves */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-light border-0">
          <h5 className="card-title mb-0">
            <i className="bi bi-list-ul me-2"></i>
            Liste des élèves
            {filteredEleves.length !== eleves.length && (
              <span className="badge bg-primary ms-2">{filteredEleves.length} résultat(s)</span>
            )}
          </h5>
        </div>
        <div className="card-body p-0">
          {filteredEleves.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-person-x text-muted" style={{ fontSize: '3rem' }}></i>
              <h4 className="text-muted mt-3">Aucun élève trouvé</h4>
              <p className="text-muted">Modifiez vos critères de recherche pour voir plus de résultats.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Élève</th>
                    <th>Classe</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEleves.map(eleve => {
                    // Gérer différentes structures de données possibles
                    const nom = eleve.nom || eleve.nom_utilisateur || '';
                    const prenom = eleve.prenom || eleve.prenom_utilisateur || '';
                    const classeNom = eleve.classe?.nom || eleve.classe?.nom_classe || eleve.nom_classe || 'Non définie';
                    const photo = eleve.photo || eleve.photo_profil || null;
                    const eleveId = eleve.id || eleve.user_id || eleve.id_utilisateur;
                    
                    return (
                      <tr key={eleveId} className="cursor-pointer" onClick={() => handleEleveSelect(eleveId)}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-circle bg-primary text-white me-3">
                              {photo ? (
                                <img
                                  src={photo}
                                  alt={`${prenom} ${nom}`}
                                  className="rounded-circle"
                                  width="40"
                                  height="40"
                                />
                              ) : (
                                <span className="fw-bold">
                                  {prenom.charAt(0)}{nom.charAt(0)}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="fw-bold">{prenom} {nom}</div>
                              <small className="text-muted">ID: {eleveId}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {classeNom}
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEleveSelect(eleveId);
                            }}
                          >
                            <i className="bi bi-graph-up me-1"></i>
                            Voir progression
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Styles CSS inline pour l'avatar
const styles = `
  .avatar-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  .cursor-pointer {
    cursor: pointer;
  }
  .cursor-pointer:hover {
    background-color: rgba(0,0,0,0.05);
  }
`;

// Injection des styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default StudentProgressionSelector;
